"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import type { TenantSettingsDto } from "@/types/tenant";

export function AnnouncementBar({ tenant }: { tenant: TenantSettingsDto }) {
  const settings = tenant.settings;
  const storageKey = `announcement-dismissed:${tenant.slug}:${settings.announcementMessage ?? ""}`;
  const [dismissed, setDismissed] = useState(true);

  const active = useMemo(() => {
    if (!settings.announcementEnabled || !settings.announcementMessage) return false;
    const now = Date.now();
    const start = parseDate(settings.announcementStartAt);
    const end = parseDate(settings.announcementEndAt);
    if (start && now < start) return false;
    if (end && now > end) return false;
    return true;
  }, [
    settings.announcementEnabled,
    settings.announcementMessage,
    settings.announcementStartAt,
    settings.announcementEndAt,
  ]);

  useEffect(() => {
    setDismissed(window.localStorage.getItem(storageKey) === "1");
  }, [storageKey]);

  if (!active || dismissed) return null;

  const close = () => {
    window.localStorage.setItem(storageKey, "1");
    setDismissed(true);
  };

  return (
    <aside aria-label="Announcement" className="border-b border-[var(--color-border)] bg-[rgb(var(--color-primary))] text-white">
      <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
        <p>
          {settings.announcementMessage}
          {settings.announcementLinkUrl && settings.announcementLinkText && (
            <>
              {" "}
              <Link className="font-semibold underline" href={settings.announcementLinkUrl}>
                {settings.announcementLinkText}
              </Link>
            </>
          )}
        </p>
        <button
          type="button"
          onClick={close}
          className="min-h-[44px] self-start rounded-[var(--border-radius)] px-3 text-sm font-medium hover:bg-white/15 sm:self-auto"
        >
          Dismiss
        </button>
      </div>
    </aside>
  );
}

function parseDate(value: unknown): number | null {
  if (typeof value !== "string" || !value.trim()) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}
